import type { ReactNode } from "react";
import { HeroSection } from "./HeroSection";
import { HeroOverline } from "./HeroOverline";
import { HeroGlyph, HeroMeta, type HeroKpi } from "./Hero";
import { cn } from "@/lib/utils";

interface CompactHeroProps {
  glyph?: ReactNode;
  overline: ReactNode;
  live?: boolean;
  title: ReactNode;
  miniBadge?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  /** KPI strip — rendered if provided. Compact heroes include a KPI strip
   *  when the mockup explicitly shows one (runs, cost, settings, etc.). */
  kpis?: HeroKpi[];
}

export function CompactHero({ glyph, overline, live, title, miniBadge, meta, actions, kpis }: CompactHeroProps) {
  return (
    <HeroSection padding="compact">
      <div className="flex justify-between items-center gap-6 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          {glyph && (
            <div
              className="w-12 h-12 rounded-[10px] flex items-center justify-center flex-shrink-0 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(255,200,61,0.2), rgba(255,200,61,0.05))",
                border: "1px solid rgba(255,200,61,0.3)",
              }}
            >
              <span
                className="absolute inset-[-1px] rounded-[10px] pointer-events-none"
                style={{ background: "radial-gradient(circle at 30% 30%, rgba(255,200,61,0.25), transparent 60%)" }}
              />
              <span className="relative text-[#ffc83d] [&_svg]:w-5 [&_svg]:h-5">{glyph}</span>
            </div>
          )}
          <div className="min-w-0">
            <HeroOverline live={live} className="mb-1 tracking-[0.24em] text-[9px]">
              {overline}
            </HeroOverline>
            <h1 className="font-brand font-extrabold text-[26px] leading-[1.05] tracking-[-0.02em] text-white m-0 flex items-center gap-3 flex-wrap">
              {title}
              {miniBadge && (
                <span
                  className="font-mono text-[10px] font-semibold px-2.5 py-1 rounded-full border border-white/15 text-white/80 uppercase tracking-[0.08em]"
                  style={{ background: "rgba(255,255,255,0.08)" }}
                >
                  {miniBadge}
                </span>
              )}
            </h1>
            {meta && <div className="mt-2 text-[11px]"><HeroMeta>{meta}</HeroMeta></div>}
          </div>
        </div>
        {actions && <div className="flex gap-2.5 items-center flex-wrap">{actions}</div>}
      </div>

      {kpis && kpis.length > 0 && (
        <div className="relative z-[2] grid grid-cols-2 md:grid-cols-4 gap-6 pt-5 mt-5 border-t border-white/[0.08]">
          {kpis.map((k) => (
            <CompactKpi key={String(k.label)} kpi={k} />
          ))}
        </div>
      )}
    </HeroSection>
  );
}

// Re-export HeroGlyph under a clearer name for compact use sites.
export { HeroGlyph as CompactGlyph };

function CompactKpi({ kpi }: { kpi: HeroKpi }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-mono text-[10px] text-white/50 tracking-[0.14em] uppercase">{kpi.label}</div>
      <div
        className={cn(
          "font-brand font-extrabold text-[34px] leading-none tracking-[-0.025em] flex items-baseline gap-1.5",
          kpi.gold ? "text-[#ffc83d]" : "text-white",
        )}
      >
        {kpi.value}
        {kpi.unit && <span className="text-sm text-white/40 font-semibold tracking-normal">{kpi.unit}</span>}
        {kpi.up && <span className="text-xs text-[#27a155] font-semibold tracking-normal">{kpi.up}</span>}
      </div>
      {kpi.sub && <div className="text-[11px] text-white/55 font-mono tracking-[0.04em] mt-0.5">{kpi.sub}</div>}
    </div>
  );
}
